import { useEffect, useState } from "react";
import api, { errMsg, exportReport } from "../lib/api";
import Modal, { Field } from "../components/Modal";
import { useAuth } from "../context/AuthContext";
import { Plus, Pencil, Trash2, Package, Search, ScanLine, Minus, Check, Tag, FileDown } from "lucide-react";
import Scanner from "../components/Scanner";
import { toast } from "sonner";

const empty = { name: "", category: "", unit: "pcs", barcode: "", tipe: "", warna: "", buy_price: 0, sell_price: 0, stock: 0, min_stock: 0 };
const norm = (s) => (s || "").trim().replace(/\s+/g, " ");

export default function Items() {
  const { user } = useAuth();
  const canEdit = !!(user?.is_owner || user?.perms?.stok_edit);
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [q, setQ] = useState("");
  const [scanOpen, setScanOpen] = useState(false);
  const [scanTarget, setScanTarget] = useState("find");
  const [drafts, setDrafts] = useState({}); // per-variant stock draft
  const [priceOpen, setPriceOpen] = useState(false);
  const [priceGroup, setPriceGroup] = useState(null);
  const [newPrice, setNewPrice] = useState("");

  const onDetected = async (code) => {
    setScanOpen(false);
    if (scanTarget === "form") { setForm((f) => ({ ...f, barcode: code })); return; }
    try {
      const { data } = await api.get(`/items/barcode/${encodeURIComponent(code)}`);
      openEdit(data);
      toast.success(`Ditemukan: ${data.name}`);
    } catch {
      setForm({ ...empty, barcode: code }); setEditId(null); setOpen(true);
      toast.message("Barang baru — lengkapi detailnya");
    }
  };

  const load = () => api.get("/items").then((r) => { setItems(r.data); setDrafts({}); }).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(empty); setEditId(null); setOpen(true); };
  const doExport = async (fmt) => {
    try { await exportReport("items", fmt, { filename: "laporan_stok" }); toast.success("Laporan diunduh"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const openEdit = (it) => { setForm({ ...empty, ...it }); setEditId(it.id); setOpen(true); };
  // add a new color under an existing product name (keeps its category/unit/tipe)
  const addVariant = (g) => {
    const base = g.items[0] || {};
    setForm({ ...empty, name: g.display, category: base.category || "", unit: base.unit || "pcs", tipe: base.tipe || "", warna: "", sell_price: base.sell_price || 0, min_stock: base.min_stock || 0, stock: 0 });
    setEditId(null); setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      buy_price: Number(form.buy_price), sell_price: Number(form.sell_price),
      stock: Number(form.stock), min_stock: Number(form.min_stock),
    };
    try {
      if (editId) await api.put(`/items/${editId}`, payload);
      else await api.post("/items", payload);
      toast.success(editId ? "Barang diperbarui" : "Barang ditambahkan");
      setOpen(false); load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const remove = async (i) => {
    if (!window.confirm(`Hapus ${i.name}${i.warna ? ` (${i.warna})` : ""}?`)) return;
    try { await api.delete(`/items/${i.id}`); toast.success("Barang dihapus"); load(); }
    catch (err) { toast.error(errMsg(err)); }
  };

  const draftVal = (i) => (drafts[i.id] !== undefined ? drafts[i.id] : i.stock);
  const setDraft = (id, v) => setDrafts((d) => ({ ...d, [id]: v }));
  const bump = (i, delta) => setDraft(i.id, Math.max(0, Number(draftVal(i) || 0) + delta));
  const saveStock = async (i) => {
    const newStock = Math.max(0, Number(draftVal(i)) || 0);
    if (newStock === i.stock) return;
    try {
      await api.put(`/items/${i.id}`, { stock: newStock });
      setItems((prev) => prev.map((x) => (x.id === i.id ? { ...x, stock: newStock } : x)));
      setDrafts((d) => { const n = { ...d }; delete n[i.id]; return n; });
      toast.success(`Stok ${i.name}${i.warna ? ` (${i.warna})` : ""} → ${newStock}`);
    } catch (err) { toast.error(errMsg(err)); }
  };

  const openPrice = (g) => { setPriceGroup(g); setNewPrice(String(g.items[0]?.sell_price ?? "")); setPriceOpen(true); };
  const savePrice = async (e) => {
    e.preventDefault();
    const val = Math.max(0, Number(newPrice) || 0);
    try {
      await api.put("/items/bulk-price", { ids: priceGroup.items.map((i) => i.id), sell_price: val });
      toast.success(`Harga ${priceGroup.display} → Rp${val.toLocaleString("id-ID")} (${priceGroup.items.length} warna)`);
      setPriceOpen(false); load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  // group items by normalized name (same code/name -> one card)
  const groupsMap = {};
  items.forEach((i) => {
    const disp = norm(i.name); const key = disp.toLowerCase();
    if (!groupsMap[key]) groupsMap[key] = { key, display: disp, items: [] };
    groupsMap[key].items.push(i);
  });
  const groups = Object.values(groupsMap)
    .map((g) => ({ ...g, items: [...g.items].sort((a, b) => (a.warna || "").localeCompare(b.warna || "")) }))
    .filter((g) => (g.display + " " + g.items.map((i) => `${i.category || ""} ${i.tipe || ""} ${i.warna || ""} ${i.barcode || ""}`).join(" ")).toLowerCase().includes(q.toLowerCase()))
    .sort((a, b) => a.display.localeCompare(b.display));

  return (
    <div className="space-y-5 animate-fadeup">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl sm:text-3xl font-display font-extrabold">Stok</h1>
          <p className="text-[#52525B] text-sm">{groups.length} produk • {items.length} varian warna</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button data-testid="export-items-excel" onClick={() => doExport("excel")} className="nb-btn px-3 py-2.5 bg-white text-sm flex items-center gap-1.5"><FileDown className="w-4 h-4" /> Excel</button>
          <button data-testid="export-items-pdf" onClick={() => doExport("pdf")} className="nb-btn px-3 py-2.5 bg-white text-sm flex items-center gap-1.5"><FileDown className="w-4 h-4" /> PDF</button>
          {canEdit && (
            <button data-testid="add-item-button" onClick={openNew} className="nb-btn px-4 py-2.5 flex items-center gap-2 text-[#0A0A0A]" style={{ background: "var(--primary)" }}>
              <Plus className="w-5 h-5" strokeWidth={2.5} /> Tambah Barang
            </button>
          )}
        </div>
      </div>

      <div className="relative">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#52525B]" />
        <input data-testid="item-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari produk / warna…" className="nb-input pl-9" />
      </div>

      {groups.length === 0 ? (
        <div className="nb-card p-10 text-center">
          <Package className="w-10 h-10 mx-auto mb-3 text-[#52525B]" />
          <p className="text-[#52525B]">Belum ada barang. Tambahkan barang pertama kamu.</p>
        </div>
      ) : (
        <div className="grid gap-4 lg:grid-cols-2 items-start">
          {groups.map((g) => {
            const unit = g.items[0]?.unit || "";
            const total = g.items.reduce((s, i) => s + (Number(i.stock) || 0), 0);
            const base = g.items[0] || {};
            return (
              <div key={g.key} data-testid={`group-${g.key}`} className="nb-card p-4">
                <div className="flex justify-between items-start gap-2 mb-3">
                  <div className="min-w-0">
                    <div className="font-display font-bold text-lg truncate">{g.display}</div>
                    <span className="label-tag">{base.category || base.tipe || "—"} • {g.items.length} warna • total {total} {unit}</span>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    {canEdit && (
                      <>
                        <button data-testid={`bulk-price-${g.key}`} onClick={() => openPrice(g)} className="nb-btn px-2.5 py-1.5 bg-white text-xs flex items-center gap-1">
                          <Tag className="w-3.5 h-3.5" /> Harga
                        </button>
                        <button data-testid={`add-warna-${g.key}`} onClick={() => addVariant(g)} className="nb-btn px-2.5 py-1.5 bg-white text-xs flex items-center gap-1">
                          <Plus className="w-3.5 h-3.5" /> Warna
                        </button>
                      </>
                    )}
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  {g.items.map((i) => {
                    const low = i.stock <= i.min_stock;
                    const changed = Number(draftVal(i)) !== Number(i.stock);
                    return (
                      <div key={i.id} data-testid={`variant-${i.id}`} className="flex items-center gap-1.5 border-2 border-[#0A0A0A] rounded-lg px-2 py-1.5">
                        <div className="min-w-0 max-w-[110px]">
                          <div className="font-bold text-xs truncate">{i.warna || "Tanpa warna"}</div>
                          {low && <span className="label-tag" style={{ color: "var(--retur)" }}>tipis (min {i.min_stock})</span>}
                        </div>
                        {canEdit ? (
                          <>
                            <div className="flex items-center gap-1 shrink-0">
                              <button data-testid={`stock-minus-${i.id}`} onClick={() => bump(i, -1)} className="w-6 h-6 border-2 border-[#0A0A0A] rounded-md flex items-center justify-center bg-white"><Minus className="w-3 h-3" /></button>
                              <input data-testid={`stock-input-${i.id}`} type="number" min="0" value={draftVal(i)} onChange={(e) => setDraft(i.id, e.target.value)}
                                className="w-11 text-center border-2 border-[#0A0A0A] rounded-md py-0.5 font-display font-extrabold text-sm" style={low ? { color: "var(--retur)" } : {}} />
                              <button data-testid={`stock-plus-${i.id}`} onClick={() => bump(i, 1)} className="w-6 h-6 border-2 border-[#0A0A0A] rounded-md flex items-center justify-center bg-white"><Plus className="w-3 h-3" /></button>
                              {changed && (
                                <button data-testid={`stock-save-${i.id}`} onClick={() => saveStock(i)} className="w-6 h-6 border-2 border-[#0A0A0A] rounded-md flex items-center justify-center text-[#0A0A0A]" style={{ background: "var(--masuk)" }}><Check className="w-3.5 h-3.5" strokeWidth={3} /></button>
                              )}
                            </div>
                            <button data-testid={`edit-item-${i.id}`} onClick={() => openEdit(i)} className="w-7 h-7 border-2 border-[#0A0A0A] rounded-lg flex items-center justify-center bg-white shrink-0"><Pencil className="w-3.5 h-3.5" /></button>
                          </>
                        ) : (
                          <span data-testid={`stock-view-${i.id}`} className="font-display font-extrabold text-sm shrink-0" style={low ? { color: "var(--retur)" } : {}}>{i.stock} {i.unit}</span>
                        )}
                        {user?.role === "admin" && (
                          <button data-testid={`delete-item-${i.id}`} onClick={() => remove(i)} className="w-7 h-7 border-2 border-[#0A0A0A] rounded-lg flex items-center justify-center shrink-0" style={{ color: "var(--retur)" }}><Trash2 className="w-3.5 h-3.5" /></button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Modal open={open} onClose={() => setOpen(false)} title={editId ? "Edit Barang" : "Tambah Barang"} testid="item-modal">
        <form onSubmit={save}>
          <Field label="Nama Barang"><input data-testid="form-item-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="nb-input" /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Kategori"><input data-testid="form-item-category" required value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="nb-input" /></Field>
            <Field label="Satuan"><input data-testid="form-item-unit" required value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} className="nb-input" /></Field>
          </div>
          <Field label="Barcode (opsional)">
            <div className="flex gap-2">
              <input data-testid="form-item-barcode" value={form.barcode || ""} onChange={(e) => setForm({ ...form, barcode: e.target.value })} className="nb-input" placeholder="Scan atau ketik kode" />
              <button type="button" data-testid="scan-barcode-form" onClick={() => { setScanTarget("form"); setScanOpen(true); }} className="nb-btn px-3 bg-white shrink-0"><ScanLine className="w-4 h-4" /></button>
            </div>
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Tipe"><input data-testid="form-item-tipe" value={form.tipe || ""} onChange={(e) => setForm({ ...form, tipe: e.target.value })} className="nb-input" placeholder="mis. Mobil-mobilan" /></Field>
            <Field label="Warna"><input data-testid="form-item-warna" value={form.warna || ""} onChange={(e) => setForm({ ...form, warna: e.target.value })} className="nb-input" placeholder="mis. Merah" /></Field>
          </div>
          <Field label="Harga Jual"><input data-testid="form-item-sell" type="number" min="0" value={form.sell_price} onChange={(e) => setForm({ ...form, sell_price: e.target.value })} className="nb-input" /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Stok Awal"><input data-testid="form-item-stock" type="number" min="0" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} className="nb-input" /></Field>
            <Field label="Stok Minimum"><input data-testid="form-item-min" type="number" min="0" value={form.min_stock} onChange={(e) => setForm({ ...form, min_stock: e.target.value })} className="nb-input" /></Field>
          </div>
          <button data-testid="save-item-button" type="submit" className="nb-btn w-full py-3 mt-2 text-[#0A0A0A]" style={{ background: "var(--primary)" }}>Simpan</button>
        </form>
      </Modal>

      <Modal open={priceOpen} onClose={() => setPriceOpen(false)} title="Ubah Harga Jual (semua warna)" testid="bulk-price-modal">
        {priceGroup && (
          <form onSubmit={savePrice}>
            <p className="text-sm text-[#52525B] mb-3">Harga baru akan diterapkan ke <b>{priceGroup.items.length} warna</b> dari produk <b>{priceGroup.display}</b>.</p>
            <Field label="Harga Jual Baru (Rp)">
              <input data-testid="bulk-price-input" type="number" min="0" required autoFocus value={newPrice} onChange={(e) => setNewPrice(e.target.value)} className="nb-input" />
            </Field>
            <button data-testid="bulk-price-save" type="submit" className="nb-btn w-full py-3 mt-1 text-[#0A0A0A]" style={{ background: "var(--primary)" }}>Terapkan ke semua warna</button>
          </form>
        )}
      </Modal>

      <Scanner open={scanOpen} onClose={() => setScanOpen(false)} onDetected={onDetected} />
    </div>
  );
}
